import React, { useState, useEffect } from 'react';
import { getRoleDisplayName } from '../../config/hospitalRoles';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  Shield,
  Settings,
  Search,
  Filter,
  Eye,
  UserCheck,
  AlertCircle,
  CheckCircle,
  Lock,
  Unlock,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';

// Inline UI components
function Card({ className = "", children }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-200 ${className}`}>
      {children}
    </div>
  );
}

function CardHeader({ children }) {
  return <div className="p-6 border-b border-gray-100">{children}</div>;
}

function CardTitle({ children }) {
  return <h3 className="text-lg font-semibold text-gray-900">{children}</h3>;
}

function CardContent({ children, className = "" }) {
  return <div className={`p-6 ${className}`}>{children}</div>;
}

function Button({ children, onClick, variant = "default", className = "", disabled = false }) {
  const baseClass = "px-4 py-2 rounded-lg font-medium transition-colors";
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    destructive: "bg-red-600 text-white hover:bg-red-700",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
    ghost: "text-gray-700 hover:bg-gray-100"
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClass} ${variants[variant]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      className={`w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
      {...props}
    />
  );
}

function Label({ children, className = "" }) {
  return <label className={`block text-sm font-medium text-gray-700 mb-1 ${className}`}>{children}</label>;
}

function Select({ children, value, onValueChange, className = "" }) {
  const options = [];

  const extractItems = (nodes) => {
    React.Children.forEach(nodes, (child) => {
      if (!child) return;
      if (child.type === SelectContent) {
        extractItems(child.props.children);
      } else if (child.type === SelectItem) {
        options.push(child);
      }
    });
  };

  extractItems(children);

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onValueChange?.(e.target.value)}
        className={`w-full appearance-none rounded-lg border border-gray-200 px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800 ${className}`}
      >
        <option value="" disabled>Choose role...</option>
        {options}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
    </div>
  );
}

function SelectItem({ value, children }) {
  return <option value={value}>{children}</option>;
}

function SelectTrigger({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

function SelectValue() {
  return null;
}

function SelectContent({ children }) {
  return children;
}

function Textarea({ className = "", ...props }) {
  return (
    <textarea
      className={`w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
      {...props}
    />
  );
}

function Dialog({ open, onOpenChange, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      {children}
    </div>
  );
}

function DialogContent({ children, className = "" }) {
  return (
    <div className={`bg-white rounded-2xl shadow-xl border border-gray-100 max-w-lg w-full mx-4 overflow-hidden animate-scaleIn ${className}`}>
      {children}
    </div>
  );
}

function DialogHeader({ children }) {
  return <div className="p-6 border-b border-gray-100 bg-slate-50/50">{children}</div>;
}

function DialogTitle({ children }) {
  return <h2 className="text-lg font-bold text-gray-900 font-sans">{children}</h2>;
}

function DialogTrigger({ children, onClick }) {
  return <div onClick={onClick}>{children}</div>;
}

const TabsContext = React.createContext(null);

function Tabs({ value, defaultValue, onValueChange, children, className = "" }) {
  const [localValue, setLocalValue] = useState(defaultValue);
  const activeValue = value !== undefined ? value : localValue;
  const handleValueChange = (val) => {
    if (onValueChange) {
      onValueChange(val);
    } else {
      setLocalValue(val);
    }
  };
  return (
    <TabsContext.Provider value={{ activeValue, onValueChange: handleValueChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

function TabsList({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

function TabsTab({ value, children, className = "" }) {
  const context = React.useContext(TabsContext);
  if (!context) return null;
  const isActive = context.activeValue === value;
  return (
    <button
      type="button"
      onClick={() => context.onValueChange?.(value)}
      className={`rounded-lg py-2.5 text-sm font-semibold transition-all ${isActive
          ? "bg-white text-blue-600 shadow-sm font-bold"
          : "text-gray-600 hover:text-gray-950 hover:bg-white/50"
        } ${className}`}
    >
      {children}
    </button>
  );
}

function TabsContent({ value, children, className = "" }) {
  const context = React.useContext(TabsContext);
  if (!context) return null;
  if (context.activeValue !== value) return null;
  return <div className={className}>{children}</div>;
}

function Badge({ children, variant = "default", className = "" }) {
  const variants = {
    default: "bg-gray-100 text-gray-800",
    success: "bg-green-100 text-green-800",
    warning: "bg-yellow-100 text-yellow-800",
    destructive: "bg-red-100 text-red-800"
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
}

function Checkbox({ checked, onCheckedChange, onChange, className = "" }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => {
        if (onCheckedChange) onCheckedChange(e.target.checked);
        if (onChange) onChange(e);
      }}
      className={`w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${className}`}
    />
  );
}

function Separator({ className = "" }) {
  return <div className={`h-px bg-gray-200 ${className}`} />;
}

function Alert({ children, variant = "default", className = "" }) {
  const variants = {
    default: "bg-gray-50 border-gray-200 text-gray-800",
    destructive: "bg-red-50 border-red-200 text-red-800"
  };
  return (
    <div className={`p-4 rounded-lg border ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
}

function AlertDescription({ children }) {
  return <div className="text-sm">{children}</div>;
}

const PERMISSION_GROUP_TITLES = {
  employee: 'Employee Management',
  department: 'Employee Management - Dept Config',
  designation: 'Employee Management - Desig Config',
  pay_scale: 'Employee Management - Scale Config',
  pensioner: 'Employee Management - Pensioner Config',
  gis: 'Employee Management - GIS Config',
  role: 'Employee Management - Role Management',
  emp: 'Employee 360',
  report: 'Reports',
  payroll: 'Payroll Management',
  pension_payroll: 'Pension Payroll Management',
  // loan: 'Loans & Advances',
  // advance: 'Loans & Advances',
  itconfig: 'IT Configuration',
  progression: 'Promotion & Increment Management',
  suspension: 'Suspension Management',
  attendance: 'Attendance',
  leave: 'Leave',
  shift: 'Shifts & Rosters',
  // reimbursement: 'Employee Reimbursement',
  pf: 'Provident Fund',
  festival_advance: 'Festival Advance',
  bank: 'Bank Loan',
  lic: 'LIC',
  arrear: 'Arrear'
  // user: 'User Account Management',
  // system: 'System Configuration & Administration',
  // holiday: 'Holiday Configuration'
};

const getGroupTitle = (resource) => {
  return PERMISSION_GROUP_TITLES[resource] || `${resource.charAt(0).toUpperCase() + resource.slice(1)} Permissions`;
};

const PERMISSION_SECTION_ORDER = [
  'Employee Management',
  'Employee 360',
  'Reports',
  'Payroll Management',
  'Pension Payroll Management',
  // 'Loans & Advances',
  'IT Configuration',
  'Promotion & Increment Management',
  'Suspension Management',
  'Attendance',
  'Leave',
  'Shifts & Rosters',
  //'Employee Reimbursement',
  'Provident Fund',
  'Festival Advance',
  'Bank Loan',
  'LIC',
  'Arrear'
];

const organizePermissionsBySection = (groupedPerms) => {
  const sections = {};
  
  // Filter out unused permissions since their modules are commented out
  const filteredPerms = {};
  Object.entries(groupedPerms).forEach(([resource, resourcePermissions]) => {
    if (resource !== 'loan' && resource !== 'advance' && 
        resource !== 'user' && resource !== 'system' && resource !== 'holiday' &&
        resource !== 'reimbursement') {
      filteredPerms[resource] = resourcePermissions;
    }
  });
  
  Object.entries(filteredPerms).forEach(([resource, resourcePermissions]) => {
    const fullTitle = getGroupTitle(resource);
    let mainHeading = fullTitle;
    let subHeading = null;
    
    if (fullTitle.includes(' - ')) {
      const parts = fullTitle.split(' - ');
      mainHeading = parts[0].trim();
      subHeading = parts.slice(1).join(' - ').trim();
    }
    
    if (!sections[mainHeading]) {
      sections[mainHeading] = [];
    }
    
    sections[mainHeading].push({
      resource,
      subHeading,
      title: fullTitle,
      permissions: resourcePermissions
    });
  });
  
  // Sort sections according to the predefined order
  const sortedSections = {};
  PERMISSION_SECTION_ORDER.forEach(heading => {
    if (sections[heading]) {
      sortedSections[heading] = sections[heading];
    }
  });
  
  // Add any sections not in the predefined order at the end
  Object.keys(sections).forEach(heading => {
    if (!sortedSections[heading]) {
      sortedSections[heading] = sections[heading];
    }
  });
  
  return sortedSections;
};

function PermissionSection({ mainHeading, groups, formData, togglePermission, isEditModal }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-4">
      <div 
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2 className="text-base font-bold text-gray-900 font-sans m-0">{mainHeading}</h2>
        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-white space-y-4">
          {groups.map(group => (
            <div key={group.resource} className="rounded-md border border-gray-100 p-4">
              <h3 className="font-semibold text-sm mb-3 font-sans text-gray-800">
                {group.subHeading || group.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(group.permissions).map(([permission, description]) => {
                  const id = `${isEditModal ? 'edit' : 'create'}-${permission}`;
                  return (
                    <div key={permission} className="flex items-start space-x-2.5 p-1 rounded hover:bg-slate-50">
                      <Checkbox
                        id={id}
                        checked={formData.permissions.includes(permission)}
                        onCheckedChange={() => togglePermission(permission)}
                        className="mt-1"
                      />
                      <div className="grid gap-0.5 leading-none">
                        <Label
                          htmlFor={id}
                          className="text-sm font-medium font-sans cursor-pointer text-gray-700"
                        >
                          {permission.split(':').slice(1).join(' ').toUpperCase() || permission}
                        </Label>
                        <p className="text-xs text-gray-500 font-sans">{description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RoleManagement = () => {
  const { refreshUser, user, hasPermission } = useAuth();
  
  const canViewRolesTab = !hasPermission || hasPermission('role:view:system') || hasPermission('role:view:custom') || hasPermission('role:update') || hasPermission('role:view:self');
  const canViewAssignTab = !hasPermission || hasPermission('role:assign');

  const [activeMainTab, setActiveMainTab] = useState(canViewRolesTab ? 'roles_list' : 'user_assignments');
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search/Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userFilterRole, setUserFilterRole] = useState('all');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    template: '',
    dashboard_tier: 'user'
  });
  const [assignedRoleId, setAssignedRoleId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkRoleId, setBulkRoleId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = (extraHeaders = {}) => {
    const token = localStorage.getItem('access_token');
    return {
      ...extraHeaders,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [rolesRes, permissionsRes, templatesRes, usersRes] = await Promise.all([
        fetch(buildApiUrl('/api/v1/roles/?hrms_only=true'), { headers: getAuthHeaders() }),
        fetch(buildApiUrl('/api/v1/roles/permissions'), { headers: getAuthHeaders() }),
        fetch(buildApiUrl('/api/v1/roles/templates'), { headers: getAuthHeaders() }),
        fetch(buildApiUrl('/api/v1/roles/users/all'), { headers: getAuthHeaders() })
      ]);

      if (!rolesRes.ok || !permissionsRes.ok || !templatesRes.ok || !usersRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const rolesData = await rolesRes.json();
      const permissionsData = await permissionsRes.json();
      const templatesData = await templatesRes.json();
      const usersData = await usersRes.json();

      setRoles(rolesData.roles || []);
      setPermissions(permissionsData.permissions || {});
      setGroupedPermissions(permissionsData.grouped_permissions || {});
      setTemplates(templatesData.templates || {});
      setUsers(usersData.users || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersRes = await fetch(buildApiUrl('/api/v1/roles/users/all'), { headers: getAuthHeaders() });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const handleCreateRole = async () => {
    try {
      const endpoint = formData.template
        ? '/api/v1/roles/from-template'
        : '/api/v1/roles/';

      const payload = formData.template
        ? {
          template_name: formData.template,
          custom_name: formData.name,
          additional_permissions: formData.additionalPermissions || [],
          removed_permissions: formData.removedPermissions || []
        }
        : {
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
          dashboard_tier: formData.dashboard_tier
        };

      const response = await fetch(buildApiUrl(endpoint), {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create role');
      }

      await fetchData();
      resetForm();
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditRole = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/roles/${selectedRole.id}`), {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          permissions: formData.permissions,
          dashboard_tier: formData.dashboard_tier
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update role');
      }

      await fetchData();
      resetForm();
      setShowEditModal(false);
      setSelectedRole(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRole = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/roles/${selectedRole.id}`), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete role');
      }

      await fetchData();
      setShowDeleteModal(false);
      setSelectedRole(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAssignRole = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/roles/users/${selectedUser.id}/role`), {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ role_id: parseInt(assignedRoleId) })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to assign role');
      }

      await fetchUsers();
      await fetchData(); // refresh count
      await refreshUser(); // refresh current user's permissions if they were the one assigned
      setShowAssignModal(false);
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBulkAssignRole = async () => {
    try {
      if (!bulkRoleId) return;
      const response = await fetch(buildApiUrl('/api/v1/roles/employees/bulk-role'), {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          employee_ids: selectedUserIds,
          role_id: parseInt(bulkRoleId)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to bulk assign role');
      }

      await fetchUsers();
      await fetchData();
      await refreshUser(); // refresh current user's permissions if they were in the bulk assignment
      setSelectedUserIds([]);
      setBulkRoleId('');
      alert('Roles assigned successfully!');
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      permissions: [],
      template: '',
      additionalPermissions: [],
      removedPermissions: [],
      dashboard_tier: 'user'
    });
  };

  const openEditModal = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || [],
      template: '',
      dashboard_tier: role.dashboard_tier || 'user'
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const openPermissionsModal = (role) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);
    setAssignedRoleId(user.role_id ? String(user.role_id) : '');
    setShowAssignModal(true);
  };

  const togglePermission = (permission) => {
    const newPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter(p => p !== permission)
      : [...formData.permissions, permission];

    setFormData({ ...formData, permissions: newPermissions });
  };

  const filteredRoles = roles.filter(role => {
    const matchesSearch = role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (role.description || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter = filterType === 'all' ||
      (filterType === 'system' && role.is_system_role) ||
      (filterType === 'custom' && !role.is_system_role);

    // Apply granular view permissions
    const canViewSystem = !hasPermission || hasPermission('role:view:system') || (hasPermission('role:view:self') && user?.role_id === role.id);
    const canViewCustom = !hasPermission || hasPermission('role:view:custom') || (hasPermission('role:view:self') && user?.role_id === role.id);
    
    if (role.is_system_role && !canViewSystem) return false;
    if (!role.is_system_role && !canViewCustom) return false;

    return matchesSearch && matchesFilter;
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.employee_name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      user.employee_code.toLowerCase().includes(userSearchQuery.toLowerCase());

    const matchesRole = userFilterRole === 'all' || String(user.role_id) === userFilterRole;

    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-sans">Role & Permission Management</h1>
          <p className="text-sm text-gray-600 font-sans">Manage custom business roles, security policies, and user assignments</p>
        </div>

        {activeMainTab === 'roles_list' && (!hasPermission || hasPermission('role:update')) && (
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-sans"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Custom Role
            </Button>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="space-y-6">
        <TabsList className={`grid w-full bg-gray-100 p-1 rounded-md ${canViewRolesTab && canViewAssignTab ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {canViewRolesTab && (
            <TabsTab value="roles_list" className="py-2 text-center font-sans font-medium">Roles & Permissions</TabsTab>
          )}
          {canViewAssignTab && (
            <TabsTab value="user_assignments" className="py-2 text-center font-sans font-medium">User Role Assignment</TabsTab>
          )}
        </TabsList>

        {canViewRolesTab && (
          <TabsContent value="roles_list" className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 font-sans"
                />
              </div>
            </div>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48 font-sans">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="system">System Roles</SelectItem>
                <SelectItem value="custom">Custom Roles</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Roles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRoles.map((role) => (
              <Card key={role.id} className="hover:shadow-lg transition-shadow border border-gray-200">
                <CardHeader className="pb-3 bg-slate-50 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <CardTitle className="text-lg font-sans font-semibold text-gray-800">{getRoleDisplayName(role.name)}</CardTitle>
                    </div>
                    {role.is_system_role && (
                      <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-700">
                        <Lock className="w-3 h-3 mr-1" />
                        System
                      </Badge>
                    )}
                  </div>
                  {role.description && (
                    <p className="text-xs text-gray-500 mt-2 font-sans">{role.description}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline" className="text-xs font-sans border-blue-200 bg-blue-50/50 text-blue-700">
                      Dashboard: {role.dashboard_tier ? role.dashboard_tier.toUpperCase() : 'USER'}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1 font-sans text-gray-600">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>{role.user_count} users</span>
                      </div>
                      <div className="flex items-center gap-1 font-sans text-gray-600">
                        <Settings className="w-4 h-4 text-gray-400" />
                        <span>{role.permissions?.length || 0} permissions</span>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPermissionsModal(role)}
                        className="font-sans text-xs"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" />
                        View
                      </Button>

                      {role.name !== "super_administrator" && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(role)}
                            disabled={hasPermission && !hasPermission('role:update')}
                            className={`font-sans text-xs ${hasPermission && !hasPermission('role:update') ? "text-gray-400 border-gray-100" : "text-blue-600 border-blue-100 hover:bg-blue-50"}`}
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>

                          {!role.is_system_role && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteModal(role)}
                              disabled={hasPermission && !hasPermission('role:delete')}
                              className={`font-sans text-xs ${hasPermission && !hasPermission('role:delete') ? "text-gray-400 border-gray-100" : "text-red-600 border-red-100 hover:bg-red-50"}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredRoles.length === 0 && (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 font-sans">No roles found matching your criteria</p>
            </div>
          )}
        </TabsContent>
        )}

        {canViewAssignTab && (
          <TabsContent value="user_assignments" className="space-y-6">
            {/* User Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search users by name, username, or code..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="pl-10 font-sans"
                  />
                </div>
              </div>

              <Select value={userFilterRole} onValueChange={setUserFilterRole}>
                <SelectTrigger className="w-56 font-sans">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(r => (
                    <SelectItem key={r.id} value={String(r.id)}>{getRoleDisplayName(r.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bulk Action Bar */}
            {selectedUserIds.length > 0 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4 animate-fadeIn">
                <div className="text-sm text-blue-800 font-sans font-medium">
                  Selected {selectedUserIds.length} employee{selectedUserIds.length > 1 ? 's' : ''} for bulk assignment
                </div>
                <div className="flex items-center gap-3">
                  <Select value={bulkRoleId} onValueChange={setBulkRoleId} className="w-56">
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose role to apply" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.filter(r => r.status === 'active').map(r => (
                        <SelectItem key={r.id} value={String(r.id)}>{getRoleDisplayName(r.name)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleBulkAssignRole}
                    disabled={!bulkRoleId || (hasPermission && !hasPermission('role:assign'))}
                    className={`${(!hasPermission || hasPermission('role:assign')) ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'} font-sans text-sm py-2 px-4`}
                  >
                    Apply Role
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedUserIds([])}
                    className="font-sans text-sm py-2 px-4 border-blue-200 hover:bg-blue-100/50 bg-white"
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}

            {/* Users Table */}
            <Card className="border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left font-sans w-12">
                        <Checkbox
                          checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedUserIds(filteredUsers.map(u => u.id));
                            } else {
                              setSelectedUserIds([]);
                            }
                          }}
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider font-sans">Employee Code</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider font-sans">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider font-sans">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider font-sans">Current Role</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider font-sans">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider font-sans">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap text-left font-sans w-12">
                          <Checkbox
                            checked={selectedUserIds.includes(user.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedUserIds([...selectedUserIds, user.id]);
                              } else {
                                setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
                              }
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 font-sans">{user.employee_code}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-sans">{user.employee_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-sans">{user.username}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-sans">
                          <Badge variant="outline" className={`border-slate-300 bg-slate-50 text-slate-700 ${user.role_name === 'super_administrator' ? 'border-amber-300 bg-amber-50 text-amber-800' : ''}`}>
                            {getRoleDisplayName(user.role_name)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-sans">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAssignModal(user)}
                            disabled={hasPermission && !hasPermission('role:assign')}
                            className={`font-sans text-xs ${hasPermission && !hasPermission('role:assign') ? "text-gray-400 cursor-not-allowed border-gray-100" : "border-blue-100 text-blue-600 hover:bg-blue-50 bg-white"}`}
                          >
                            <UserCheck className="w-3.5 h-3.5 mr-1" />
                            Assign Role
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-sans">No users found matching your criteria</p>
                </div>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create Role Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b shrink-0">
            <DialogTitle className="font-sans text-xl">Create Custom Role</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col p-6 pt-4">
            <Tabs defaultValue="basic" className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded border shrink-0">
                <TabsTab value="basic" className="font-sans font-medium py-1.5 text-center">Basic Info</TabsTab>
                <TabsTab value="permissions" className="font-sans font-medium py-1.5 text-center">Permissions Selection</TabsTab>
              </TabsList>

              <TabsContent value="basic" className="flex-1 overflow-y-auto mt-4 pr-2 space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="name" className="font-sans">Role Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. PF Handler"
                    className="font-sans mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="font-sans">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter what this role handles..."
                    rows={3}
                    className="font-sans mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dashboard_tier" className="font-sans font-medium">Dashboard Type</Label>
                  <Select
                    value={formData.dashboard_tier}
                    onValueChange={(val) => setFormData({ ...formData, dashboard_tier: val })}
                    className="mt-1"
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin Dashboard</SelectItem>
                      <SelectItem value="checker">Checker Dashboard</SelectItem>
                      <SelectItem value="maker">Maker Dashboard</SelectItem>
                      <SelectItem value="user">User Dashboard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="flex-1 overflow-y-auto mt-4 pr-2">
              {Object.entries(organizePermissionsBySection(groupedPermissions)).map(([mainHeading, groups]) => (
                <PermissionSection
                  key={mainHeading}
                  mainHeading={mainHeading}
                  groups={groups}
                  formData={formData}
                  togglePermission={togglePermission}
                  isEditModal={false}
                />
              ))}
            </TabsContent>
          </Tabs>
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 shrink-0">
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }} className="font-sans">
              Cancel
            </Button>
            <Button onClick={handleCreateRole} className="bg-blue-600 hover:bg-blue-700 text-white font-sans">
              Save Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Role Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b shrink-0">
            <DialogTitle className="font-sans text-xl">Edit Role: {getRoleDisplayName(selectedRole?.name)}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editDescription" className="font-sans">Description</Label>
                <Textarea
                  id="editDescription"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="font-sans mt-1"
                />
              </div>
              <div>
                <Label htmlFor="editDashboardTier" className="font-sans font-medium">Dashboard Type</Label>
                <Select
                  value={formData.dashboard_tier}
                  onValueChange={(val) => setFormData({ ...formData, dashboard_tier: val })}
                  className="mt-1"
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin Dashboard</SelectItem>
                    <SelectItem value="checker">Checker Dashboard</SelectItem>
                    <SelectItem value="maker">Maker Dashboard</SelectItem>
                    <SelectItem value="user">User Dashboard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="font-sans">Permissions Selection</Label>
              <div className="mt-2 pr-1 space-y-2">
                {Object.entries(organizePermissionsBySection(groupedPermissions)).map(([mainHeading, groups]) => (
                  <PermissionSection
                    key={mainHeading}
                    mainHeading={mainHeading}
                    groups={groups}
                    formData={formData}
                    togglePermission={togglePermission}
                    isEditModal={true}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 shrink-0">
            <Button variant="outline" onClick={() => {
              setShowEditModal(false);
              setSelectedRole(null);
              resetForm();
            }} className="font-sans">
              Cancel
            </Button>
            <Button onClick={handleEditRole} className="bg-blue-600 hover:bg-blue-700 text-white font-sans">
              Update Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-sans">Delete Custom Role</DialogTitle>
          </DialogHeader>

          <div className="p-6 space-y-4">
            <p className="font-sans text-sm text-gray-600">Are you sure you want to permanently delete the custom role <strong>{getRoleDisplayName(selectedRole?.name)}</strong>?</p>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="font-sans">
                This action cannot be undone. Make sure no users are assigned to this role before deleting.
              </AlertDescription>
            </Alert>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 shrink-0">
            <Button variant="outline" onClick={() => {
              setShowDeleteModal(false);
              setSelectedRole(null);
            }} className="font-sans">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteRole}
              className="bg-red-600 hover:bg-red-700 text-white font-sans"
            >
              Delete Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Role Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-sans">Assign User Role</DialogTitle>
          </DialogHeader>

          <div className="p-6 space-y-4">
            <div className="p-3 bg-slate-50 rounded border text-sm space-y-1">
              <div className="font-sans text-gray-600">User: <strong className="text-gray-800">{selectedUser?.employee_name} ({selectedUser?.username})</strong></div>
              <div className="font-sans text-gray-600">Employee Code: <strong className="text-gray-800">{selectedUser?.employee_code}</strong></div>
              <div className="font-sans text-gray-600">Current Role: <strong className="text-gray-800">{getRoleDisplayName(selectedUser?.role_name)}</strong></div>
            </div>

            <div className="space-y-1.5">
              <Label className="font-sans">Select HRMS Role</Label>
              <Select value={assignedRoleId} onValueChange={setAssignedRoleId}>
                <SelectTrigger className="w-full font-sans mt-1">
                  <SelectValue placeholder="Choose new role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.filter(r => r.status === 'active').map(r => (
                    <SelectItem key={r.id} value={String(r.id)}>{getRoleDisplayName(r.name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 shrink-0">
            <Button variant="outline" onClick={() => {
              setShowAssignModal(false);
              setSelectedUser(null);
            }} className="font-sans">
              Cancel
            </Button>
            <Button onClick={handleAssignRole} className="bg-blue-600 hover:bg-blue-700 text-white font-sans" disabled={!assignedRoleId}>
              Assign Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Permissions Modal */}
      <Dialog open={showPermissionsModal} onOpenChange={setShowPermissionsModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-sans flex items-center gap-2">
              Role Permissions: {getRoleDisplayName(selectedRole?.name)}
              {selectedRole?.is_system_role && (
                <Badge variant="secondary" className="bg-gray-100 text-gray-600 text-xs">
                  <Lock className="w-3 h-3 mr-1" />
                  System
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {selectedRole?.description && (
              <p className="text-sm text-gray-600 font-sans">{selectedRole.description}</p>
            )}

            <div className="flex items-center gap-4 text-xs">
              <div className="flex items-center gap-1 font-sans text-gray-500">
                <Users className="w-3.5 h-3.5" />
                <span>{selectedRole?.user_count || 0} users assigned</span>
              </div>
              <div className="flex items-center gap-1 font-sans text-gray-500">
                <Settings className="w-3.5 h-3.5" />
                <span>{selectedRole?.permissions?.length || 0} active permissions</span>
              </div>
            </div>

            <Separator />

            {selectedRole?.permissions?.includes('*') ? (
              <Alert className="border-emerald-100 bg-emerald-50 text-emerald-800">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="font-sans font-medium">
                  This role possesses full Super Administrator system privileges. All calculations, configurations, and user settings are accessible.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
                {Object.entries(groupedPermissions).map(([resource, resourcePermissions]) => {
                  const rolePermissions = selectedRole?.permissions || [];
                  const relevantPerms = Object.keys(resourcePermissions).filter(p =>
                    rolePermissions.includes(p)
                  );

                  if (relevantPerms.length === 0) return null;

                  return (
                    <Card key={resource} className="p-4 border border-gray-100">
                      <h3 className="font-semibold text-sm mb-3 capitalize font-sans text-gray-800">{resource} Permissions</h3>
                      <div className="grid grid-cols-1 gap-2.5">
                        {relevantPerms.map((permission) => (
                          <div key={permission} className="flex items-center gap-2 text-xs">
                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                            <span className="font-mono text-gray-700 bg-slate-50 px-1 py-0.5 rounded border">{permission}</span>
                            <span className="text-gray-500 font-sans">
                              {resourcePermissions[permission]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => {
              setShowPermissionsModal(false);
              setSelectedRole(null);
            }} className="font-sans">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagement;